// Onglet « Critères » d'une campagne (F-027) : catalogue fermé de critères admissibles, alertes,
// versions et aperçu de la présentation publique.
import { useEffect, useState } from "react";
import { post, put } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { dateTime, money } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, ErrorBox, Input, Loading, Notice, Select, Table, useToast } from "@core/ui";
import { ApiError } from "@core/api";

type Criteria = {
  loyer_reference: string;
  ratio_revenus_min_centiemes: number | null;
  taux_effort_max_bp: number | null;
  revenus_pris_en_compte: string[];
  garant_exige: string;
  garant_situations: string[];
  ratio_garant_min_centiemes: number | null;
  visale_accepte: boolean;
  garanties_acceptees: string[];
  assurance_loyers_impayes: boolean;
};

type Alert = { level: string; field: string; message: string };

type Resp = {
  campagne: { title: string; rent_cents: number; charges_cents: number; public_slug: string | null };
  criteres: Criteria | null;
  version: number | null;
  par_defaut: Criteria;
  resume_public: string[] | null;
  alertes: Alert[];
  catalogue: { revenus: { code: string; label: string; obligatoire: boolean }[]; garanties: { code: string; label: string }[]; situations: { code: string; label: string }[] };
  versions: { version: number; created_at: string; author: string | null; alertes: Alert[] }[];
  regles: string;
};

/** « 3 » ou « 3,5 » → centièmes ; vide → null. */
function toHundredths(v: string): number | null | undefined {
  const t = v.trim().replace(",", ".").replace("%", "").trim();
  if (!t) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return undefined;
  return Math.round(parseFloat(t) * 100);
}
function fromHundredths(v: number | null): string {
  if (v === null || v === undefined) return "";
  return (v / 100).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
}

function toggle(list: string[], code: string, on: boolean) {
  return on ? [...new Set([...list, code])] : list.filter((x) => x !== code);
}

const ALERT_TONE: Record<string, "erreur" | "attention" | "info"> = { non_admissible: "erreur", attention: "attention", information: "info" };

export function CriteresTab({ orgId, campaignId }: { orgId: string; campaignId: string }) {
  const org = useOrg();
  const path = `/api/v1/orgs/${orgId}/campaigns/${campaignId}/criteres`;
  const q = useGet<Resp>(path);
  const invalidate = useInvalidate();
  const toast = useToast();
  const [c, setC] = useState<Criteria | null>(null);
  const [ratio, setRatio] = useState("");
  const [effort, setEffort] = useState("");
  const [ratioG, setRatioG] = useState("");
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [summary, setSummary] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.data) return;
    const base = q.data.criteres ?? q.data.par_defaut;
    setC(base);
    setRatio(fromHundredths(base.ratio_revenus_min_centiemes));
    setEffort(fromHundredths(base.taux_effort_max_bp));
    setRatioG(fromHundredths(base.ratio_garant_min_centiemes));
    setAlerts(q.data.alertes);
    setSummary(q.data.resume_public);
  }, [q.data]);

  if (q.isLoading || !c) return q.error ? <ErrorBox error={q.error} /> : <Loading />;
  const d = q.data!;
  const canEdit = org.can("operate");
  const parsed = { r: toHundredths(ratio), e: toHundredths(effort), g: toHundredths(ratioG) };
  const invalidNumber = parsed.r === undefined || parsed.e === undefined || parsed.g === undefined;
  const payload = (): Criteria => ({ ...c, ratio_revenus_min_centiemes: parsed.r ?? null, taux_effort_max_bp: parsed.e ?? null, ratio_garant_min_centiemes: parsed.g ?? null });
  const rent = c.loyer_reference === "charges_comprises" ? d.campagne.rent_cents + d.campagne.charges_cents : d.campagne.rent_cents;

  const submit = async (save: boolean) => {
    setBusy(true);
    setError(null);
    try {
      if (save) {
        const res = await put<{ alertes: Alert[]; version: number; resume_public: string[] }>(path, payload());
        setAlerts(res.alertes);
        setSummary(res.resume_public);
        toast(`Critères enregistrés (version ${res.version}). Relancez les contrôles des dossiers déjà analysés pour les appliquer.`);
        invalidate(path, `/api/v1/orgs/${orgId}/applications`);
      } else {
        const res = await post<{ alertes: Alert[]; resume_public?: string[] }>(`${path}/verifier`, payload());
        setAlerts(res.alertes);
        if (res.resume_public) setSummary(res.resume_public);
      }
    } catch (e) {
      setError(e);
      if (e instanceof ApiError && e.fields.length) setAlerts(e.fields.map((f) => ({ level: "non_admissible", field: f.field, message: f.message })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <Notice tone="info" title="Des critères explicites, appliqués de la même façon à chaque dossier">
        Seuls des critères financiers admissibles sont proposés ; aucun critère libre. Chaque dossier reçoit un résultat par critère (satisfait, non satisfait, indéterminé), jamais une note. Vous décidez.
      </Notice>
      <Card title={`Critères financiers${d.version ? ` · version ${d.version}` : " · non définis"}`}>
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            submit(true);
          }}
        >
          <div className="form-row">
            <Select
              label="Loyer de référence"
              value={c.loyer_reference}
              disabled={!canEdit}
              options={[
                { value: "charges_comprises", label: `Charges comprises (${money(d.campagne.rent_cents + d.campagne.charges_cents)})` },
                { value: "hors_charges", label: `Hors charges (${money(d.campagne.rent_cents)})` },
              ]}
              onChange={(e) => setC({ ...c, loyer_reference: e.target.value })}
            />
            <Input
              label="Revenus minimaux (fois le loyer)"
              inputMode="decimal"
              value={ratio}
              disabled={!canEdit}
              help={parsed.r ? `Soit ${money(Math.round((rent * parsed.r) / 100))} par mois. Laisser vide pour ne pas l’utiliser.` : "Laisser vide pour ne pas l’utiliser."}
              error={parsed.r === undefined ? "Indiquez un nombre, par exemple 3 ou 2,5." : undefined}
              onChange={(e) => setRatio(e.target.value)}
            />
            <Input
              label="Taux d’effort maximal (%)"
              inputMode="decimal"
              value={effort}
              disabled={!canEdit}
              help="Part du loyer dans les revenus. Facultatif."
              error={parsed.e === undefined ? "Indiquez un pourcentage, par exemple 33." : undefined}
              onChange={(e) => setEffort(e.target.value)}
            />
          </div>
          <fieldset className="stack-sm">
            <legend>
              <strong>Revenus pris en compte</strong>
            </legend>
            {d.catalogue.revenus.map((r) => (
              <Checkbox
                key={r.code}
                label={r.label}
                help={r.obligatoire ? "Toujours pris en compte : les exclure peut constituer une discrimination." : undefined}
                checked={c.revenus_pris_en_compte.includes(r.code)}
                onChange={(on) => (canEdit && !r.obligatoire ? setC({ ...c, revenus_pris_en_compte: toggle(c.revenus_pris_en_compte, r.code, on) }) : undefined)}
              />
            ))}
          </fieldset>
          <div className="form-row">
            <Select
              label="Garant ou garantie"
              value={c.garant_exige}
              disabled={!canEdit}
              options={[
                { value: "jamais", label: "Non exigé" },
                { value: "selon_situation", label: "Selon la situation" },
                { value: "toujours", label: "Toujours attendu" },
              ]}
              onChange={(e) => setC({ ...c, garant_exige: e.target.value })}
            />
            <Input
              label="Revenus minimaux du garant (fois le loyer)"
              inputMode="decimal"
              value={ratioG}
              disabled={!canEdit}
              help="Facultatif, évalué séparément du foyer."
              error={parsed.g === undefined ? "Indiquez un nombre." : undefined}
              onChange={(e) => setRatioG(e.target.value)}
            />
          </div>
          {c.garant_exige === "selon_situation" ? (
            <fieldset className="stack-sm">
              <legend>
                <strong>Situations pour lesquelles un garant est attendu</strong>
              </legend>
              {d.catalogue.situations.map((s) => (
                <Checkbox key={s.code} label={s.label} checked={c.garant_situations.includes(s.code)} onChange={(on) => canEdit && setC({ ...c, garant_situations: toggle(c.garant_situations, s.code, on) })} />
              ))}
            </fieldset>
          ) : null}
          <fieldset className="stack-sm">
            <legend>
              <strong>Garanties acceptées</strong>
            </legend>
            {d.catalogue.garanties.map((g) => (
              <Checkbox key={g.code} label={g.label} checked={c.garanties_acceptees.includes(g.code)} onChange={(on) => canEdit && setC({ ...c, garanties_acceptees: toggle(c.garanties_acceptees, g.code, on), visale_accepte: g.code === "visale" ? on : c.visale_accepte })} />
            ))}
            <Checkbox
              label="J’ai souscrit une assurance loyers impayés"
              help="Dans ce cas, une caution ne peut être exigée que d’un étudiant ou d’un apprenti (article 22-1 de la loi du 6 juillet 1989)."
              checked={c.assurance_loyers_impayes}
              onChange={(on) => canEdit && setC({ ...c, assurance_loyers_impayes: on })}
            />
          </fieldset>
          {alerts.length ? (
            <div className="stack-sm" aria-live="polite">
              {alerts.map((a, i) => (
                <Notice key={i} tone={ALERT_TONE[a.level] ?? "info"} title={a.level === "non_admissible" ? "Critère non admissible" : a.level === "attention" ? "À vérifier" : undefined}>
                  {a.message}
                </Notice>
              ))}
            </div>
          ) : null}
          <ErrorBox error={error && !(error instanceof ApiError && error.fields.length) ? error : null} />
          {canEdit ? (
            <div className="row">
              <Button type="submit" busy={busy} disabled={invalidNumber}>
                Enregistrer une nouvelle version
              </Button>
              <Button variant="secondaire" busy={busy} disabled={invalidNumber} onClick={() => submit(false)}>
                Vérifier sans enregistrer
              </Button>
            </div>
          ) : null}
        </form>
      </Card>
      {summary ? (
        <Card title="Présentation aux candidats">
          <p className="small muted">Texte affiché sur la page de candidature{d.campagne.public_slug ? "" : " lorsque la campagne sera publiée"}.</p>
          <ul>
            {summary.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </Card>
      ) : null}
      {d.versions.length ? (
        <Card title="Versions">
          <Table caption="Versions des critères">
            <thead>
              <tr>
                <th>Version</th>
                <th>Enregistrée le</th>
                <th>Par</th>
                <th>Alertes</th>
              </tr>
            </thead>
            <tbody>
              {d.versions.map((v) => (
                <tr key={v.version}>
                  <td>v{v.version}</td>
                  <td>{dateTime(v.created_at)}</td>
                  <td>{v.author ?? "—"}</td>
                  <td>{v.alertes.length ? <Badge tone="attention">{v.alertes.length}</Badge> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="small muted">
            Règles d’évaluation : <span className="mono">{d.regles}</span>
          </p>
        </Card>
      ) : null}
    </div>
  );
}
